<template>

  <v-row class="mb-6" align="center" no-gutters wrap>
    <!-- Controles à esquerda -->
    <v-col cols="12" md="8">
      <div class="d-flex flex-wrap align-center gap-3">
        <v-btn-toggle v-model="viewMode" mandatory variant="outlined" density="compact" class="mr-2">
          <v-btn value="grid">
            <v-icon>mdi-view-grid-outline</v-icon>
            <v-tooltip activator="parent" location="top">
              Visualização em Grid
            </v-tooltip>
          </v-btn>
          <v-btn value="kanban">
            <v-icon>mdi-view-column-outline</v-icon>
            <v-tooltip activator="parent" location="top">
              Visualização Kanban
            </v-tooltip>
          </v-btn>
        </v-btn-toggle>

        <v-btn variant="text" :loading="state.loading" @click="refreshActivities">
          <v-icon>mdi-refresh</v-icon>
          <v-tooltip activator="parent" location="top">
            Atualizar
          </v-tooltip>
        </v-btn>
      </div>
    </v-col>

    <!-- Botão à direita -->
    <v-col cols="12" md="4" class="d-flex justify-end align-center">
      <v-btn v-if="authStore.isAdmin()" color="primary" prepend-icon="mdi-plus" @click="showAddDialog = true">
        Nova Atividade
      </v-btn>
    </v-col>
  </v-row>

  <!-- Filtros -->
  <ActivityFilters v-model="filters" :assignees="availableAssignees" @filters-changed="handleFiltersChanged" />

  <!-- Conteúdo Principal -->
  <v-fade-transition mode="out-in">
    <!-- Visualização em Grid -->
    <div v-if="viewMode === 'grid'" key="grid">
      <v-row v-if="!state.loading && activitiesWithCalculatedPercentages.length">
        <v-col v-for="activity in activitiesWithCalculatedPercentages" :key="activity.id" cols="12" md="6" lg="6">
          <ActivityCard :activity="activity" @edit="handleEditActivity" @view="handleViewActivity"
            @add-subtask="handleAddSubtask" @view-history="handleViewHistory" @edit-subtask="handleEditSubtask"
            @view-subtask-history="handleViewSubtaskHistory" @delete-subtask="handleDeleteSubtask"
            @delete="handleDeleteActivity" @duplicate="handleDuplicateActivity"
            @update-subtask-status="handleUpdateSubtaskStatus" @update-activity-status="handleUpdateActivityStatus" />
        </v-col>
      </v-row>
    </div>

    <div v-else-if="viewMode === 'kanban'" key="kanban">
      <KanbanBoard :activities="activitiesWithCalculatedPercentages" :loading="state.loading"
        @view-activity="handleViewActivity" @view-history="handleViewHistory" @activity-moved="handleActivityMoved"
        @add-activity="handleAddActivity" @edit-activity="handleEditActivity" @delete-activity="handleDeleteActivity"
        @duplicate-activity="handleDuplicateActivity" @add-subtask="handleAddSubtask" @edit-subtask="handleEditSubtask"
        @view-subtask-history="handleViewSubtaskHistory" @delete-subtask="handleDeleteSubtask"
        @update-subtask-status="handleUpdateSubtaskStatus" @update-activity-status="handleUpdateActivityStatus" />
    </div>
  </v-fade-transition>

  <div v-if="state.loading" class="text-center pa-8" style="height:100vh">
    <v-skeleton-loader type="card" class="mb-5"></v-skeleton-loader>
    <v-skeleton-loader type="card" class="mb-5"></v-skeleton-loader>
    <v-skeleton-loader type="card"></v-skeleton-loader>
  </div>

  <v-empty-state v-else-if="!activitiesWithCalculatedPercentages.length && !state.loading"
    headline="Nenhuma atividade encontrada"
    :title="hasActiveFilters ? 'Nenhuma atividade corresponde aos filtros' : 'Comece criando sua primeira atividade'"
    :text="hasActiveFilters ? 'Tente ajustar os filtros ou limpar todos os filtros' : 'Use o botão Nova Atividade para começar'">
    <template #actions>
      <v-btn v-if="hasActiveFilters" variant="outlined" @click="clearAllFilters">
        Limpar Filtros
      </v-btn>
      <v-btn v-if="authStore.isAdmin()" color="primary" @click="showAddDialog = true">
        Nova Atividade
      </v-btn>
    </template>
  </v-empty-state>

  <v-alert v-if="state.error" type="error" variant="tonal" class="mt-4" closable @click:close="state.error = null">
    {{ state.error }}
  </v-alert>

  <ActivityDialog v-model="showAddDialog" :activity="selectedActivity" :is-viewing="isViewing"
    :parent-id="selectedParentId" @save="handleSaveActivity" @close="handleCloseDialog" />

  <ConfirmDialog v-model="showDeleteDialog" :title="deleteTarget?.isSubtask ? 'Excluir Subtarefa' : 'Excluir Atividade'"
    :text="deleteTarget?.isSubtask ?
      'Tem certeza que deseja excluir esta subtarefa? Esta ação não pode ser desfeita.' :
      'Tem certeza que deseja excluir esta atividade e todas as suas subtarefas? Esta ação não pode ser desfeita.'"
    @confirm="confirmDelete" />

  <LogsDialog v-model="showHistoryDialog" :activity-id="selectedHistoryActivityId" :is-subtask="isHistorySubtask" />

</template>

<script setup>
import { ref, computed, onMounted, watch, provide, nextTick } from 'vue'
import { useActivities } from '@/composables/useActivities'
import { usePercentageCalculations } from '@/composables/usePercentageCalculations'
import { useExpansionState } from '@/composables/useExpansionState'
import { useAuthStore } from '@/stores/auth'
import ActivityFilters from '@/components/application/activities/ActivityFilters.vue'
import ActivityCard from '@/components/application/activities/ActivityCard.vue'
import ActivityDialog from '@/components/application/activities/ActivityDialog.vue'
import ConfirmDialog from '@/components/application/activities/ConfirmDialog.vue'
import KanbanBoard from '@/components/application/activities/kanban/KanbanBoard.vue'
import LogsDialog from '@/components/application/logs/LogsDialog.vue'

import { useToastStore } from '@/stores/toast';

const expansionState = useExpansionState()

const authStore = useAuthStore()
const activitiesManager = useActivities()
const toast = useToastStore()

const {
  state,
  filteredActivities,
  mainActivitiesWithHierarchy,
  activityStats,
  statusOptions,
  setFilter,
  clearFilters,
  fetchActivities,
  createActivity,
  createSubtask,
  updateActivity,
  deleteActivity,
  updateActivityStatus,
  updateSubtaskStatus,
  getActivityById,
  getNextOrder
} = useActivities()

const {
  updateAllActivitiesPercentages,
  calculateProgressStats,
  validateActivityStructure
} = usePercentageCalculations()

provide('statusOptions', statusOptions)
provide('getNextOrder', getNextOrder)
provide('activities', activitiesManager)
provide('expansionState', expansionState)

const viewMode = ref('grid')
const filters = ref({
  search: '',
  status: null,
  assignee: null,
  dateRange: null,
  priority: null
})

const showAddDialog = ref(false)
const showDeleteDialog = ref(false)
const selectedActivity = ref(null)
const selectedParentId = ref(null)
const deleteTarget = ref(null)
const isViewing = ref(false)

const showHistoryDialog = ref(false)
const selectedHistoryActivityId = ref(null)
const isHistorySubtask = ref(false)

const activitiesWithCalculatedPercentages = computed(() => {
  const validActivities = filteredActivities.value.filter(activity =>
    validateActivityStructure(activity)
  )

  return updateAllActivitiesPercentages(validActivities)
})

const progressStats = computed(() => {
  return calculateProgressStats(activitiesWithCalculatedPercentages.value)
})

const hasActiveFilters = computed(() =>
  Object.values(filters.value).some(v => v !== null && v !== '' && v !== undefined)
)

const availableAssignees = computed(() => {
  const assignees = new Set()

  const extractAssignees = (activities) => {
    activities.forEach(activity => {
      if (activity.assignees && Array.isArray(activity.assignees)) {
        activity.assignees.forEach(assignee => {
          assignees.add(JSON.stringify({ name: assignee, id: assignee }))
        })
      }
      if (activity.children && activity.children.length > 0) {
        extractAssignees(activity.children)
      }
    })
  }

  extractAssignees(state.hierarchyActivities)

  return Array.from(assignees).map(assigneeStr => {
    const assignee = JSON.parse(assigneeStr)
    return {
      name: assignee.name || assignee,
      id: assignee.id || assignee,
      avatar: assignee.avatar || null
    }
  })
})

const refreshActivities = async () => {
  const currentExpandedActivities = new Set(expansionState.expandedActivities.value)
  const currentExpandedSubtasks = new Set(expansionState.expandedSubtasks.value)

  await fetchActivities()

  await nextTick()
  expansionState.expandedActivities.value = currentExpandedActivities
  expansionState.expandedSubtasks.value = currentExpandedSubtasks
}

const handleFiltersChanged = (newFilters) => {
  Object.entries(newFilters).forEach(([key, value]) => {
    setFilter(key, value)
  })
}

const clearAllFilters = () => {
  filters.value = {
    search: '',
    status: null,
    assignee: null,
    dateRange: null,
    priority: null
  }
  clearFilters()
}

const handleEditActivity = (activityId) => {
  selectedActivity.value = getActivityById(activityId)
  selectedParentId.value = null
  showAddDialog.value = true
  isViewing.value = false
}

const handleViewActivity = (activityId) => {
  selectedActivity.value = getActivityById(activityId)
  selectedParentId.value = null
  isViewing.value = true
  showAddDialog.value = true
}


const handleEditSubtask = (subtaskId) => {

  const findSubtaskInHierarchy = (activities, targetId) => {
    for (const activity of activities) {

      if (activity.id == targetId) {
        return activity
      }

      if (activity.children && activity.children.length > 0) {
        const found = findSubtaskInHierarchy(activity.children, targetId)
        if (found) return found
      }

      if (activity.subtasks && activity.subtasks.length > 0) {
        const subtask = activity.subtasks.find(sub => sub.id == targetId)
        if (subtask) return subtask
      }
    }
    return null
  }

  const subtask = findSubtaskInHierarchy(state.hierarchyActivities, subtaskId)

  if (subtask) {
    selectedActivity.value = subtask

    if (subtask.ordem) {
      const orderParts = subtask.ordem.split('.')
      if (orderParts.length > 1) {

        const parentOrder = orderParts.slice(0, -1).join('.')

        const findParentByOrder = (activities, targetOrder) => {
          for (const activity of activities) {
            if (activity.ordem === targetOrder) {
              return activity
            }
            if (activity.children && activity.children.length > 0) {
              const found = findParentByOrder(activity.children, targetOrder)
              if (found) return found
            }
          }
          return null
        }

        const parentActivity = findParentByOrder(state.hierarchyActivities, parentOrder)
        selectedParentId.value = parentActivity ? parentActivity.id : null
      } else {
        selectedParentId.value = null
      }
    } else {
      selectedParentId.value = null
    }

    showAddDialog.value = true
    isViewing.value = false
  } else {
    console.error('Subtarefa não encontrada:', subtaskId)
    toast.error('Subtarefa não encontrada')
  }
}

const handleAddActivity = (options = {}) => {
  selectedActivity.value = {
    status: options.status || 'not_started',
    progress: 0,
    ...options
  }
  selectedParentId.value = null
  showAddDialog.value = true
}

const handleViewHistory = (activityId) => {
  selectedHistoryActivityId.value = activityId
  isHistorySubtask.value = false
  showHistoryDialog.value = true
}

const handleViewSubtaskHistory = (subtaskId) => {
  selectedHistoryActivityId.value = subtaskId
  isHistorySubtask.value = true
  showHistoryDialog.value = true
}

const handleAddSubtask = (parentId) => {
  const parentActivity = getActivityById(parentId)

  selectedActivity.value = {
    status: 'not_started',
    progress: 0
  }
  selectedParentId.value = parentId
  showAddDialog.value = true

}

const handleDeleteActivity = (activityId) => {
  const activity = getActivityById(activityId)
  deleteTarget.value = {
    id: activityId,
    isSubtask: false,
    title: activity?.title || 'Atividade'
  }
  showDeleteDialog.value = true
}

const handleDeleteSubtask = (subtaskId) => {

  let subtask = null
  for (const activity of state.activities) {
    if (activity.subtasks) {
      subtask = activity.subtasks.find(sub => sub.id === subtaskId)
      if (subtask) break
    }
  }

  if (subtask) {
    deleteTarget.value = {
      id: subtaskId,
      isSubtask: true,
      title: subtask.title || 'Subtarefa'
    }
    showDeleteDialog.value = true
  }
}

const handleDuplicateActivity = async (activityId) => {
  try {
    const original = getActivityById(activityId)
    if (original) {
      const duplicate = {
        ...original,
        id: undefined,
        title: `${original.title} (Cópia)`,
        status: 'not_started',
        progress: 0

      }
      await createActivity(duplicate)
    }
  } catch (err) {
    console.error('Erro ao duplicar atividade:', err)
  }
}

const handleActivityMoved = async ({ activity, toStatus }) => {
  try {
    await updateActivityStatus(activity.id, toStatus)
  } catch (err) {
    console.error('Erro ao mover atividade:', err)
  }
}

const handleUpdateActivityStatus = async (data) => {
  try {
    await updateActivityStatus(data.activityId, data.status)

  } catch (err) {
    toast.error(err)
  }
}

const handleUpdateSubtaskStatus = async (data) => {
  try {
    await updateSubtaskStatus(data.subtaskId, data.status)

  } catch (err) {
    toast.error(err)
  }
}

const handleSaveActivity = async (activityData) => {
  try {
    const currentExpandedActivities = new Set(expansionState.expandedActivities.value)
    const currentExpandedSubtasks = new Set(expansionState.expandedSubtasks.value)

    if (selectedActivity.value?.id) {
      await updateActivity(selectedActivity.value.id, activityData)
    } else if (selectedParentId.value) {
      await createSubtask(selectedParentId.value, activityData)
    } else {
      await createActivity(activityData)
    }

    toast.success('Atividade salva com sucesso!')

    await nextTick()

    expansionState.expandedActivities.value = currentExpandedActivities
    expansionState.expandedSubtasks.value = currentExpandedSubtasks

    handleCloseDialog()
  } catch (err) {
    console.error('Erro ao salvar:', err)
    toast.error(err)
  }
}

const handleCloseDialog = async () => {
  showAddDialog.value = false
  await nextTick()
  selectedActivity.value = null
  selectedParentId.value = null
  isViewing.value = false
}

const confirmDelete = async () => {
  try {
    await deleteActivity(deleteTarget.value.id)
    showDeleteDialog.value = false
    deleteTarget.value = null

    toast.success('Atividade excluída com sucesso!')
  } catch (err) {
    console.error('Erro ao excluir:', err)
  }
}

watch(filters, handleFiltersChanged, { deep: true })

onMounted(() => {
  fetchActivities()
})

defineExpose({
  fetchActivities,
  addActivity: handleAddActivity,
  progressStats: progressStats,
  getNextOrder: getNextOrder,
  viewHistory: handleViewHistory
})
</script>

<style scoped>
/* Transições suaves */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.v-fade-transition-enter-from,
.v-fade-transition-leave-to {
  opacity: 0;
}

/* Responsividade */
@media (max-width: 600px) {
  .pa-6 {
    padding: 1rem !important;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }
}

/* Estados especiais */
.v-empty-state {
  min-height: 400px;
}

/* Animações para estatísticas */
.text-h5 {
  transition: all 0.3s ease;
}

/* Estilos para o card de estatísticas */
.text-h4 {
  transition: all 0.3s ease;
}

.text-h6 {
  transition: all 0.3s ease;
}
</style>
